/**
 * A minimal past/present/future undo stack.
 *
 * Two deliberate properties, both of which the previous implementation got
 * wrong:
 *
 * 1. Snapshots are stored **by reference**, not through
 *    `JSON.parse(JSON.stringify(...))`. The design object holds `File`
 *    handles, and a `File` JSON-serialises to `{}` — which is still truthy, so
 *    undoing after an upload used to restore an empty object that passed every
 *    guard and then threw on first property access.
 *
 * 2. Recording is a pure function called from inside the reducer, so undoing
 *    cannot feed back in as a fresh edit. Previously an effect mirrored state
 *    into the history after every change, including the changes undo itself
 *    dispatched — which truncated the redo branch every single time, making
 *    redo permanently unreachable.
 */
export interface History<T> {
  readonly past: readonly T[];
  readonly present: T;
  readonly future: readonly T[];
  /**
   * Identifier of the edit that produced `present`. Consecutive edits sharing
   * a tag collapse into one undo step, so dragging a slider or typing a URL
   * does not bury the previous action under a hundred entries.
   */
  readonly lastTag: string | null;
}

export function createHistory<T>(present: T): History<T> {
  return { past: [], present, future: [], lastTag: null };
}

export function record<T>(
  history: History<T>,
  present: T,
  tag: string | null,
  limit: number,
): History<T> {
  if (Object.is(present, history.present)) return history;

  // Same tag as the previous edit: amend in place rather than push a step.
  if (tag !== null && tag === history.lastTag) {
    return { ...history, present, future: [] };
  }

  const past = [...history.past, history.present];
  return {
    past: past.length > limit ? past.slice(past.length - limit) : past,
    present,
    future: [],
    lastTag: tag,
  };
}

export function canUndo<T>(history: History<T>): boolean {
  return history.past.length > 0;
}

export function canRedo<T>(history: History<T>): boolean {
  return history.future.length > 0;
}

export function undo<T>(history: History<T>): History<T> {
  const previous = history.past.at(-1);
  if (previous === undefined) return history;
  return {
    past: history.past.slice(0, -1),
    present: previous,
    future: [history.present, ...history.future],
    lastTag: null,
  };
}

export function redo<T>(history: History<T>): History<T> {
  const [next, ...rest] = history.future;
  if (next === undefined) return history;
  return {
    past: [...history.past, history.present],
    present: next,
    future: rest,
    lastTag: null,
  };
}
